import BasicTooltipWithOffset from "@/pages/human-resources/time-management/components/BasicTooltipWithOffset";
import IconButton from "@/shared/components/generic/button/IconButton";
import IconPrinter from "@/shared/components/generic/icons/IconPrinter";
import { Level, type Size } from "@/shared/components/generic/UiEnums";
import "./SchedulePrintMenu.scss";
import { useRef, useState, type ReactNode } from "react";
import type { PrintMenuItem } from "@/pages/human-resources/time-management/components/print-menu/PrintMenuItem";
import PopMenu from "@/shared/components/generic/pop-menu/PopMenu";
import { useTranslation } from "react-i18next";
import useVisibleDraftSchedules from "@/pages/human-resources/time-management/model/schedule-view/useVisibleDraftSchedules";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";

interface Properties<ActionType extends string> {
  buttonSize: Size;
  menuItems: PrintMenuItem<ActionType>[];
  onItemClicked: (action: ActionType) => void;
}

export default function SchedulePrintMenu<ActionType extends string>(
  props: Properties<ActionType>,
): ReactNode {
  const { canPrint } = useScheduleUserPermissions();

  const needsValidation = useVisibleDraftSchedules().length > 0;
  const { t } = useTranslation(["common", "humanResources"]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const tooltipLabel =
    needsValidation ?
      t("humanResources:timeManagement.print.needsValidation")
    : t("common:actions.print");

  return (
    <div ref={anchorRef}>
      <BasicTooltipWithOffset title={tooltipLabel}>
        <IconButton
          icon={IconPrinter}
          level={Level.TERTIARY}
          size={props.buttonSize}
          disabled={needsValidation || !canPrint}
          onClick={() => {
            setMenuOpen(true);
          }}
        />
      </BasicTooltipWithOffset>

      <PopMenu
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => {
          setMenuOpen(false);
        }}
        placement={"bottom"}
      >
        <div className="schedule-print-menu">
          {props.menuItems.map((item) => (
            <div
              key={item.action}
              onClick={() => {
                props.onItemClicked(item.action);
                setMenuOpen(false);
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </PopMenu>
    </div>
  );
}
